import type { ShopProduct } from '~/services/bootstrap'
import {
  deriveProductPricing,
  type InvalidSaleReason,
  type ProductPricing,
} from './effective-price'

// The two soft currencies a catalogue product can cost — Classic, Daily Picks,
// and hero cards alike; 'iap' products live only in the currency catalogue.
export type CatalogueCurrency = 'coin' | 'gem'

// What a catalogue card's product costs (GLOSSARY.md "Free product",
// "Effective price"): nothing, or a soft-currency price whose effective
// amount follows its sale window. A free product carries neither a currency
// nor pricing — its price and sale fields are never read.
export type CataloguePrice = { kind: 'free' } | PaidCataloguePrice

export interface PaidCataloguePrice {
  currency: CatalogueCurrency
  kind: 'paid'
  pricing: ProductPricing
}

// The purchase-type and price checks every card collection applies before
// visibility (GLOSSARY.md "Hydrated Shop product"): a card sells for coins,
// Time Crystals, or nothing, and a paid price must be positive — zero is
// malformed, never read as free.
export function cataloguePriceFailure(
  product: ShopProduct,
): 'invalid_price' | 'invalid_purchase_type' | null {
  if (product.purchase_type === 'free') return null
  if (product.purchase_type !== 'coin' && product.purchase_type !== 'gem') {
    return 'invalid_purchase_type'
  }
  if (!Number.isFinite(product.price) || product.price <= 0) {
    return 'invalid_price'
  }
  return null
}

// The price of a product that passed cataloguePriceFailure. Invalid sale data
// never omits a paid product — the sale is ignored and reported, and the
// product sells at its original price (ADR 0027).
export function deriveCataloguePrice(
  product: ShopProduct,
  onInvalidSale: (reason: InvalidSaleReason) => void,
): CataloguePrice {
  if (product.purchase_type === 'free') return { kind: 'free' }
  return {
    currency: product.purchase_type as CatalogueCurrency,
    kind: 'paid',
    pricing: deriveProductPricing(product, onInvalidSale),
  }
}
